import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { FacilityAllianceMembersBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsInstitutionsSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as FacilityAllianceMemberActions from "../../../actions/facilityAllianceMembers";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { InstitutionLookupsLayout } from "../../index";

import "./FacilityAllianceMembersPage.scss";

interface FacilityAllianceMembersPageProps {
    formFilter: IFormFilter;
    facilityAllianceMember: Dtos.FacilityAllianceMember[];
    loadingFacilityAllianceMembers: boolean;
    loadFacilityAllianceMembersSuccess: boolean;
    loadFacilityAllianceMembersFailure: boolean;
    loadFacilityAllianceMembersErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearFacilityAllianceMembers: FacilityAllianceMemberActions.IFacilityAllianceMemberClearActionCreator;
    loadFacilityAllianceMembers: FacilityAllianceMemberActions.IFacilityAllianceMemberLoadActionCreator;
    filter: FacilityAllianceMemberActions.IFacilityAllianceMemberFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const FacilityAllianceMembersPage: FC<FacilityAllianceMembersPageProps> = ({
    formFilter,
    facilityAllianceMember,
    loadingFacilityAllianceMembers,
    loadFacilityAllianceMembersSuccess,
    loadFacilityAllianceMembersFailure,
    loadFacilityAllianceMembersErrorCode,
    permissions,
    pageSize,
    clearFacilityAllianceMembers,
    loadFacilityAllianceMembers,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showFacilityAllianceMembers =
        !loadingFacilityAllianceMembers && loadFacilityAllianceMembersSuccess;


    // Load facility alliance members
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsInstitutionsSubMenu(permissions));
            setBreadcrumbs(FacilityAllianceMembersBreadcrumbs());

            loadFacilityAllianceMembers();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsInstitutionsSubMenu,
        permissions,
        setBreadcrumbs,
        FacilityAllianceMembersBreadcrumbs,
        loadFacilityAllianceMembers,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadFacilityAllianceMembersFailure) {
            navigate(`/error/${loadFacilityAllianceMembersErrorCode}`);
        }
    }, [
        loadFacilityAllianceMembersFailure,
        loadFacilityAllianceMembersErrorCode,
        navigate,
    ]);

    // Cleanup effect to clear the facility alliance members on component unmount
    useEffect(() => {
        return () => {
            clearFacilityAllianceMembers();
        };
    }, [clearFacilityAllianceMembers]);

    const createTable = useMemo(() => {
        if (!facilityAllianceMember || facilityAllianceMember.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no site locations to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = facilityAllianceMember.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={facilityAllianceMember}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination
                            ? pageSize
                            : facilityAllianceMember.length
                    }
                    pageSize={
                        showPagination
                            ? pageSize
                            : facilityAllianceMember.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no site locations to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (e, handleOriginal) => {
                                const url: string = `/settings/institution/facilityalliancemembers/${rowInfo.original.id}`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        facilityAllianceMember,
        pageSize,
        PAGE_SIZE_OPTIONS,
        setPageSize,
        navigate,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Facility Alliance Members</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.FacilityAllianceMembersCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    "/settings/institution/facilityalliancemembers/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showFacilityAllianceMembers ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showFacilityAllianceMembers ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        formFilter,
        filter,
        permissions,
        showFacilityAllianceMembers,
        createTable,
    ]);

    return (
        <InstitutionLookupsLayout
            loading={loadingFacilityAllianceMembers}
            permission={Dtos.Permission.FacilityAllianceMembersView}
        >
            {renderContent}
        </InstitutionLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<FacilityAllianceMembersPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.facilityAllianceMembers.formFilter,
        facilityAllianceMember: state.facilityAllianceMembers.formFilteredData,
        loadingFacilityAllianceMembers:
            state.facilityAllianceMembers.loadState &&
            state.facilityAllianceMembers.loadState.status ==
                RequestState.Pending,
        loadFacilityAllianceMembersSuccess:
            state.facilityAllianceMembers.loadState &&
            state.facilityAllianceMembers.loadState.status ==
                RequestState.Success,
        loadFacilityAllianceMembersFailure:
            state.facilityAllianceMembers.loadState &&
            state.facilityAllianceMembers.loadState.status ==
                RequestState.Failure,
        loadFacilityAllianceMembersErrorCode:
            state.facilityAllianceMembers.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<FacilityAllianceMembersPageProps> => {
    return {
        loadFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMemberActions.LoadFacilityAllianceMember,
            dispatch
        ),
        clearFacilityAllianceMembers: bindActionCreators(
            FacilityAllianceMemberActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(
            FacilityAllianceMemberActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(FacilityAllianceMembersPage);
